import { add } from 'date-fns';
import { noop } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useUuid } from '~/eds/src/hooks/useUuid';
import { Balance } from '~/features/billing/CreditBalance/types';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import { TAG_BY_CREDIT_SUMMARY } from '~/redux/api/endpoints/codecash';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

interface FormState {
  credits: Nullable<number>;
  effectiveDate: Nullable<Date>;
  expirationDate: Nullable<Date>;
  notes: Nullable<string>;
}

interface Props {
  onClose: () => void;

  balance?: Balance;
}

const InitialFormState: FormState = {
  credits: 0,
  effectiveDate: new Date(),
  expirationDate: add(new Date(), { years: 1 }),
  notes: '',
};

export const OneTimeCreditPanel = ({ balance, onClose }: Props) => {
  const intl = useIntl();
  const [id, updateId] = useUuid();
  const { RETRY_ERROR } = useErrors();
  const { params } = useRouting();
  const [createOneTimeCreditBalance, { isLoading: isCreateBalanceLoading }] =
    api.endpoints.createOneTimeCreditBalance.useMutation();

  const [editOneTimeCreditBalance, { isLoading: isEditingBalanceLoading }] =
    api.endpoints.editOneTimeCreditBalance.useMutation();
  const [formState, setFormState] = useState<FormState>(
    balance
      ? {
          credits: 0,
          effectiveDate: balance.effectiveDate ?? null,
          expirationDate: balance.expirationDate ?? null,
          notes: balance.notes,
        }
      : InitialFormState,
  );

  const dispatch = useDispatch();
  const { toast } = useToast();

  const { credits, effectiveDate, expirationDate, notes } = formState;

  const editBalance = () =>
    editOneTimeCreditBalance({
      balanceId: balance!.id,
      clientId: Number(params.clientId),
      body: {
        data: {
          type: 'balances',
          attributes: {
            credits: credits!,
            expirationDate: expirationDate!.toISOString(),
            idempotencyKey: `balance_${id}`,
            notes: notes!,
          },
        },
      },
    })
      .unwrap()
      .then((data) => {
        if (data) {
          toast({
            message: intl.formatMessage({
              id: 'OneTimeCreditPanel.creditsEditedSuccess',
              defaultMessage: 'Credits edited successfully',
              description: 'Success message when credits are edited',
            }),
            status: 'success',
          });
          dispatch(api.util.invalidateTags([TAG_BY_CREDIT_SUMMARY]));
        }
      })
      .catch((err) => {
        toast({
          message: err[0].detail ?? RETRY_ERROR,
          status: 'danger',
        });
      })
      .finally(onClose);

  const createBalance = () =>
    createOneTimeCreditBalance({
      clientId: Number(params.clientId),
      body: {
        data: {
          type: 'balances',
          attributes: {
            credits: credits!,
            expirationDate: expirationDate!.toISOString(),
            idempotencyKey: `balance_${id}`,
            notes: notes!,
          },
        },
      },
    })
      .unwrap()
      .then((data) => {
        if (data) {
          toast({
            message: intl.formatMessage({
              id: 'OneTimeCreditPanel.creditsAddedSuccess',
              defaultMessage: 'Credits added successfully',
              description: 'Success message when credits are added',
            }),
            status: 'success',
          });
          dispatch(api.util.invalidateTags([TAG_BY_CREDIT_SUMMARY]));
          onClose();
        }
      })
      .catch((err) => {
        toast({
          message: err[0].detail ?? RETRY_ERROR,
          status: 'danger',
        });
      });

  return (
    <Panel
      footer={{
        actions: [
          {
            level: 'tertiary' as const,
            text: intl.formatMessage({
              id: 'OneTimeCreditPanel.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            }),
            onClick: onClose,
          },
          {
            disabled: credits! <= 0,
            isLoading: isCreateBalanceLoading || isEditingBalanceLoading,
            level: 'primary' as const,
            text: balance
              ? intl.formatMessage({
                  id: 'OneTimeCreditPanel.updateCreditsButton',
                  defaultMessage: 'Update Credits',
                  description: 'Button text to update credits',
                })
              : intl.formatMessage({
                  id: 'OneTimeCreditPanel.addCreditsButton',
                  defaultMessage: 'Add Credits',
                  description: 'Button text to add credits',
                }),
            tooltip:
              credits! <= 0
                ? intl.formatMessage({
                    id: 'OneTimeCreditPanel.fieldsValidationTooltip',
                    defaultMessage:
                      'Please make sure all fields are filled correctly',
                    description: 'Tooltip when form fields are invalid',
                  })
                : '',
            onClick: () => (balance ? editBalance() : createBalance()),
          },
        ],
      }}
      hidden={{
        onHide: onClose,
      }}
      position="right"
      title={
        balance
          ? intl.formatMessage({
              id: 'OneTimeCreditPanel.updateOneTimeCreditsTitle',
              defaultMessage: 'Update One-Time Credits',
              description: 'Panel title for updating one-time credits',
            })
          : intl.formatMessage({
              id: 'OneTimeCreditPanel.addOneTimeCreditsTitle',
              defaultMessage: 'Add One-Time Credits',
              description: 'Panel title for adding one-time credits',
            })
      }
      width="m"
    >
      <Layout preset="form-fields">
        {balance && (
          <FormField<number, false>
            readOnly
            input={NumberInput}
            label={intl.formatMessage({
              id: 'OneTimeCreditPanel.currentCreditAmountLabel',
              defaultMessage: 'Current Credit Amount',
              description: 'Label for current credit amount field',
            })}
            name="Current Credit Amount "
            value={balance.credits}
            onChange={noop}
          />
        )}
        <FormField<number, false>
          required
          input={NumberInput}
          inputProps={{
            min: 1,
            max: 1_000_000_000, // 1 billion per product requirement
          }}
          label={
            balance
              ? intl.formatMessage({
                  id: 'OneTimeCreditPanel.newCreditAmountLabel',
                  defaultMessage: 'New Credit Amount',
                  description: 'Label for new credit amount field',
                })
              : intl.formatMessage({
                  id: 'OneTimeCreditPanel.creditsLabel',
                  defaultMessage: 'Credits',
                  description: 'Label for credits field',
                })
          }
          name={balance ? 'New Credit Amount' : 'Credits'}
          value={credits}
          onChange={(credits) => {
            updateId();
            setFormState({ ...formState, credits });
          }}
        />
        <FormField<Date, false>
          disabled={true}
          required
          input={DateInput}
          label={intl.formatMessage({
            id: 'OneTimeCreditPanel.effectiveDateLabel',
            defaultMessage: 'Effective Date',
            description: 'Label for effective date field',
          })}
          name="Effective Date"
          value={effectiveDate}
          onChange={noop}
        />
        <FormField<Date, false>
          required
          input={DateInput}
          inputProps={{
            min: new Date(),
          }}
          label={intl.formatMessage({
            id: 'OneTimeCreditPanel.expirationDateLabel',
            defaultMessage: 'Expiration Date',
            description: 'Label for expiration date field',
          })}
          name="Expiration Date"
          width="100%"
          value={expirationDate}
          onChange={(expirationDate) => {
            updateId();
            setFormState({ ...formState, expirationDate });
          }}
        />
        <FormField<string, false>
          footer={intl.formatMessage({
            id: 'OneTimeCreditPanel.notesMaxCharactersFooter',
            defaultMessage: 'Maximum 300 characters',
            description: 'Footer text for notes field character limit',
          })}
          input={TextArea}
          inputProps={{
            maxLength: 300,
          }}
          label={intl.formatMessage({
            id: 'OneTimeCreditPanel.notesLabel',
            defaultMessage: 'Notes',
            description: 'Label for notes field',
          })}
          maxLength={300}
          name="Notes"
          value={notes}
          onChange={(notes) => {
            updateId();
            setFormState({ ...formState, notes });
          }}
        />
      </Layout>
    </Panel>
  );
};
